import * as React from 'react';
import { matchPath } from 'react-router-dom';
import {
    Create, Edit, useNotify, useRefresh, useRedirect, TabbedForm, TextInput, required,
    NumberInput,
    Toolbar, SaveButton, ListButton, SelectInput,
    useCreate, useUpdate, DeleteWithConfirmButton, useRecordContext, minValue, maxValue, email
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Grid } from '@mui/material';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { TimeInput } from 'react-admin';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useState } from 'react';
import { IData } from '../../types';
import './style.css';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const USTimeLabel = 'You need select Team to watch current US time';
const timeFormat = 'HH:mm';
const LATime = 'America/Los_Angeles';
const VNTime = 'Asia/Ho_Chi_Minh';
const PHTime = 'Asia/Manila';

interface WorkingTimeRow extends IData {
    id: number;
    From?: string;
    To?: string;
    FromHelpText?: string;
    ToHelpText?: string;
}

const PostForm: React.FC<IData> = ({ objFilter }) => {
    const isNew = matchPath('/user/create/*', location.pathname);
    const Form: React.FC = () => {
        const record = useRecordContext();
        const redirect = useRedirect();
        const notify = useNotify();
        const refresh = useRefresh();
        const [updating] = !isNew ? useUpdate() : useCreate();
        const [userTeamId, setUserTeamId] = useState<number>(record?.teamId || 0);

        const USTimeParser = (time: string, userLocation: string): Dayjs => {
            console.log('time', time);
            const utcTimeFrom = dayjs.tz(time, timeFormat, userLocation).utc();
            const UStime = utcTimeFrom.tz(LATime);
            return UStime;
        };

        const GetHelperText = (time: string): string => {
            if (time === '') return '...';
            const userLocation = userTeamId === 1 ? VNTime : PHTime;
            const timeFormat = USTimeParser(time, userLocation);
            return `US Time: ${timeFormat.format('h:mm A')}`;
        };

        const recordWorkingTime: WorkingTimeRow[] = !record
            ? [{ id: 0, From: '08:00', To: '17:00', FromHelpText: USTimeLabel, ToHelpText: USTimeLabel }]
            : record?.workingTimes !== ''
                ? JSON.parse(record.workingTimes).map((x: IData, index: number) => ({ ...x, id: index, FromHelpText: GetHelperText(x.From), ToHelpText: GetHelperText(x.To) })) : [{ id: 0 }];
        const [timeRange, setTimeRange] = useState<WorkingTimeRow[]>(recordWorkingTime);

        const sortByTime = (a: WorkingTimeRow, b: WorkingTimeRow): number => {
            const timeFormat = 'HH:mm';
            const timeA = dayjs(a.From, timeFormat);
            const timeB = dayjs(b.From, timeFormat);
            return timeA.isBefore(timeB) ? -1 : timeA.isAfter(timeB) ? 1 : 0;
        };

        const handleSubmit = (data: IData): void => {
            Object.keys(data).map(key => {
                if (key.includes('from_') || key.includes('to_')) {
                    delete data[key];
                }
            });
            let sortedData = timeRange.slice().sort(sortByTime).map(({ id: _id, ...x }) => x);
            data.workingTimes = (sortedData?.length === 1 && sortedData[0].From === '' && sortedData[0].To === '') ? null : JSON.stringify(sortedData);

            updating('User', { id: data.id, data: data },
                {
                    onSuccess: () => {
                        notify('Changes updated', { type: 'success' });
                        redirect('/User');
                        refresh();
                    },
                    onError: (error: any) => {
                        notify(`Save fail: ${error.message}`, { type: 'error' });
                    }
                }
            );
        };

        const MyToolbar: React.FC = () => {
            return (
                <Toolbar>
                    <SaveButton />
                    <ListButton />
                    {
                        !isNew && <DeleteWithConfirmButton translateOptions={{ name: record?.id }} />
                    }
                </Toolbar>
            );
        };

        const formatUSTimeZone = (teamId: number): void => {
            if (isNew && !teamId) return;
            dayjs.extend(utc);
            dayjs.extend(timezone);
            setTimeRange(prev => (
                prev.map(x => {
                    const userLocation = teamId === 1 ? VNTime : PHTime;
                    return {
                        ...x,
                        FromHelpText: x.From ? `US Time: ${USTimeParser(x.From, userLocation).format('h:mm A')}` : '...',
                        ToHelpText: x.To ? `US Time: ${USTimeParser(x.To, userLocation).format('h:mm A')}` : '...',
                    };
                }))
            );
        };

        const hanldeChangeTeam = (event: IData): void => {
            formatUSTimeZone(event.target.value);
            setUserTeamId(event.target.value);
        };

        return (
            <TabbedForm defaultValues={({ market: 0 })} toolbar={<MyToolbar />} mode="onBlur" reValidateMode="onBlur" onSubmit={handleSubmit}>
                <TabbedForm.Tab label="User" sx={{ display: 'grid' }}>
                    <Grid container columnSpacing={2}>
                        <Grid size={6}>
                            <TextInput source="email" label="Email" validate={[required(), email()]} resettable />
                        </Grid>
                        <Grid size={6}>
                            <TextInput source="aliasName" label="Alias Name" validate={required()} resettable />
                        </Grid>
                    </Grid>
                    <Grid container columnSpacing={2}>
                        <Grid size={4}>
                            <TextInput source="firstName" label="First Name" resettable />
                        </Grid>
                        <Grid size={4}>
                            <TextInput source="midName" label="Mid Name" resettable />
                        </Grid>
                        <Grid size={4}>
                            <TextInput source="lastName" label="Last Name" resettable />
                        </Grid>
                    </Grid>
                    <Grid container columnSpacing={2}>
                        <Grid size={6}>
                            <TextInput source="fullName" label="Full Name" resettable />
                        </Grid>
                        <Grid size={6}>
                            <TextInput source="phone" label="Phone" resettable />
                        </Grid>
                    </Grid>
                    <Grid container columnSpacing={2}>
                        <Grid size={3}>
                            <SelectInput validate={required()} label="Team" source="teamId"
                                choices={objFilter.Team}
                                translateChoice={false} resettable fullWidth onChange={hanldeChangeTeam} />
                        </Grid>
                        <Grid size={3}>
                            <NumberInput step={0.1} source="indicator" label="Indicator" validate={[minValue(0), maxValue(1)]} max={1} min={0} />
                        </Grid>
                        <Grid size={3}>
                            <NumberInput source="order" label="Order" />
                        </Grid>
                        <Grid size={3}>
                            <SelectInput validate={required()} label="Status" source="status"
                                choices={objFilter.Status}
                                translateChoice={false} resettable />
                        </Grid>
                    </Grid>
                    <Grid container size={12} columnSpacing={2}>
                    </Grid>
                    <MyTimePicker timeRange={timeRange} setTimeRange={setTimeRange} GetHelperText={GetHelperText} />
                </TabbedForm.Tab>
            </TabbedForm>
        );
    };

    return isNew ? (<Create ><Form /></Create>) : (<Edit ><Form /></Edit>);
};

const MyTimePicker: React.FC<IData> = (params) => {
    const { timeRange, setTimeRange, GetHelperText } = params;
    const { clearErrors, setValue, unregister } = useFormContext();

    const handleWorkTime = (event: any, action: string, id: number): void => {
        switch (action) {
            case 'from':
                // Update state when change From time
                setValue(`from_${id}`, event.target.value, { shouldDirty: true });
                setTimeRange((prev: WorkingTimeRow[]) => prev.map((item) => (item.id === id ? { ...item, From: event.target.value, FromHelpText: GetHelperText(event.target.value) } : item)));
                break;
            case 'to':
                // Update state when change To time
                setValue(`to_${id}`, event.target.value, { shouldDirty: true });
                setTimeRange((prev: WorkingTimeRow[]) => prev.map((item) => (item.id === id ? { ...item, To: event.target.value, ToHelpText: GetHelperText(event.target.value) } : item)));
                break;
            case 'add':
                const cloneRange = timeRange.find((x: WorkingTimeRow) => x.id === id);
                setTimeRange((prev: WorkingTimeRow[]) => [...prev, { ...cloneRange, id: timeRange[timeRange.length - 1].id + 1 }]);
                break;
            case 'remove':
                // // Dual behavior: delete row when multiple rows exist, clear values when only one row exists
                // setTimeRange(prev => (prev.length <= 1 ?
                //     prev.map(item => (item.id === id ? { ...item, From: '', To: '', FromHelpText: '...', ToHelpText: '...' } : item))
                //     : prev.filter(x => x.id !== id)));
                // if (timeRange.length === 1) {
                //     setValue(`from_${id}`, '');
                //     setValue(`to_${id}`, '');
                // }
                if (timeRange.length > 1) {
                    // case 1: More than 1 row -> Remove the row
                    // Use unregister to remove fields from the form
                    unregister(`from_${id}`);
                    unregister(`to_${id}`);
                    // remove the row from local state to update UI
                    setTimeRange((prev: WorkingTimeRow[]) => prev.filter((x) => x.id !== id));
                } else {
                    // case 2: Only 1 row -> Clear the values of that row
                    // Use setValue to clear the values in the form and INFORM THE FORM THAT IT HAS CHANGED
                    setValue(`from_${id}`, null, { shouldDirty: true });
                    setValue(`to_${id}`, null, { shouldDirty: true });
                    // Update local state to reflect the changes in the UI
                    setTimeRange((prev: WorkingTimeRow[]) => prev.map((item) => (item.id === id ? { ...item, From: '', To: '', FromHelpText: '...', ToHelpText: '...' } : item)));
                }
                break;
        }

    };


    const validateWorkingTimes = (_value: any, allValues: IData, data: IData): string | undefined => {
        if (allValues[`from_${data.validateid}`] === null && allValues[`to_${data.validateid}`] === null) {
            clearErrors(`from_${data.validateid}`);
            clearErrors(`to_${data.validateid}`);
        }
        let comparePrefix = data.source.includes('from') ? 'to' : 'from';
        const compareValue = allValues[`${comparePrefix}_${data.validateid}`];
        const timeFormat = 'HH:mm';
        const compareTime = typeof (compareValue) === 'object' ? dayjs(compareValue) : dayjs(compareValue, timeFormat);
        const checkTime = dayjs(data.value, timeFormat);
        if (!checkTime.isValid() && !compareTime.isValid()) return undefined;
        if (!checkTime.isValid()) return 'Please complete the start and end time';
        if (checkTime.isValid() && compareTime.isValid()) {
            if (comparePrefix === 'to' && compareTime.isBefore(checkTime)) {
                return 'The start time must be less than end time';
            } else if (comparePrefix === 'from' && compareTime.isAfter(checkTime)) {
                return 'The end time must be greater than start time';
            }
            clearErrors(`${comparePrefix}_${data.validateid}`);//clear error in compare time if checkTime is ok
        }

        return undefined;
    };

    return (
        <>
            {timeRange?.map((item: WorkingTimeRow) =>
                <Grid container size={12} columnSpacing={2} key={item.id}>
                    <Grid size={3}>
                        <TimeInput
                            className="my-custom-class"
                            defaultValue={item.From || null}
                            value={item.From}
                            source={`from_${item.id}`}
                            label="From"
                            {...({ validateid: item.id } as any)}
                            onChange={(event: any) => handleWorkTime(event, 'from', item.id)}
                            onError={(err: any) => console.log('error:', err)}
                            validate={validateWorkingTimes}
                            helperText={item.FromHelpText}
                        />
                    </Grid>
                    <Grid size={3}>
                        <TimeInput
                            defaultValue={item.To || null}
                            value={item.To}
                            source={`to_${item.id}`}
                            label="To"
                            {...({ validateid: item.id } as any)}
                            onChange={(event: any) => handleWorkTime(event, 'to', item.id)}
                            validate={validateWorkingTimes}
                            helperText={item.ToHelpText}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Stack direction="row" spacing={1} sx={{ marginTop: '15px' }}>
                            <Button variant="contained" onClick={() => handleWorkTime(item, 'add', item.id)} endIcon={<AddCircleOutlineIcon />}>Add</Button>
                            <Button variant="contained" onClick={() => handleWorkTime(item, 'remove', item.id)} endIcon={<DeleteOutlineIcon />}>Remove</Button>
                        </Stack>
                    </Grid>
                </Grid>
            )}
        </>
    );
};
export default PostForm;
